import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component, EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  SimpleChanges
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { NavatticExperimentService } from 'src/app/services/navattic-experiment.service';
import { DeviceDetector } from 'src/app/services/segment/device-detector';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { TrialWidgetService } from 'src/app/services/trial-widget/trial-widget.service';
import { AuthState } from 'src/app/store/auth/auth.state';

interface ITutorialWidgetConditions {
  subscriptionStatus?: string | string[];
  pricingPlan?: string | string[];
  trackingMode?: string | string[];
  minUserCount?: number;
  companySize?: string | string[];
}

@UntilDestroy()
@Component({
  selector: 'app-floating-widget',
  templateUrl: './floating-widget.component.html',
  styleUrls: ['./floating-widget.component.scss'],
})
export class FloatingWidgetComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() widgetWidth?: number = 380;
  @Input() widgetHeight?: number = 200;
  @Input() isDemoDialogOpen = false;
  @Input() shouldShowStartTutorialWidget = false;
  @Input() isNavatticExperiment = false;

  @Output() isWidgetDragging: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() widgetHeightChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output() navatticDialogOpened: EventEmitter<void> = new EventEmitter<void>();
  @Output() navatticDialogClosed: EventEmitter<void> = new EventEmitter<void>();
  @Output() startTutorialRequested: EventEmitter<void> = new EventEmitter<void>();

  public isDemoDialogOpen$ = new BehaviorSubject<boolean>(false);
  public canShowTrialWidget$: Observable<boolean>;
  public canShowPaidWidget$: Observable<boolean>;
  public canShowStartTutorialWidget$: Observable<boolean>;
  public canShowBookCallWidget$: Observable<boolean>;
  public position: { top: number, right: number } = this.getPosition();
  public isDragging = false;
  public offsetX = 0;
  public offsetY = 0;
  public isMobile = false;
  public touchMoved = false; // Track if a drag occurred
  public floatingTrialWidgetHeight = 40;

  public isPathExcluded = false;
  public isPathExcluded$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  drag = false;

  private canShowStartTutorialWidget = false;
  private canShowBookCallWidget = false;

  public shouldShowFloatingWidget$: Observable<boolean>;
  public shouldShowTrialWidget$: Observable<boolean>;
  public shouldShowStartTutorialWidget$: Observable<boolean>;

  public isScreenWideEnoughForTour$: Observable<boolean>;

  constructor(
    public deviceDetector: DeviceDetector,
    private readonly segment: SegmentService,
    private readonly store: Store,
    private readonly trialWidgetService: TrialWidgetService,
    private readonly navatticExperimentService: NavatticExperimentService,
    private readonly router: Router,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly growthbookService: GrowthBookService,
  ) { }

  ngOnInit(): void {
    this.isMobile$ = this.deviceDetector.isBrowsingFromMobile().pipe(
      untilDestroyed(this),
    );

    this.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
      this.adjustForMobile();
    });

    this.position = this.getPosition();

    this.checkPathExclusion();
    this.initializeObservables();
  }

  private initializeObservables(): void {
    const company$ = this.store.select(AuthState.company);
    const isAuthorized$ = this.store.select(AuthState.authorized) as Observable<boolean>;
    const activeTutorial$ = this.navatticExperimentService.getActiveTutorial$('B');
    const isActiveVariantB$ = this.navatticExperimentService.isVariant$('B');

    const growthBookInitialized$ = this.growthbookService.initialized.pipe(
      filter(initialized => initialized),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.canShowTrialWidget$ = combineLatest([
      company$,
      isAuthorized$,
      growthBookInitialized$,
    ]).pipe(
      map(([company, isAuthorized]) => ['trial', 'paid'].includes(company?.subscription?.status) && isAuthorized),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.canShowPaidWidget$ = combineLatest([
      company$,
      growthBookInitialized$,
    ]).pipe(
      map(([company]) => 'paid' === company?.subscription?.status),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // Eligibility for Book a Call FAB (before relocation rules)
    const eligibleBookCall$ = combineLatest([
      isActiveVariantB$,
      this.growthbookService.getFeature('book-call-sales-v2', false),
      company$,
      growthBookInitialized$,
    ]).pipe(
      map(([isVariantB, hasBookACallAccess, company]) => {
        return !!company && isVariantB && hasBookACallAccess;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // Eligibility for Start Tutorial FAB (before relocation rules)
    const eligibleStartTutorial$ = combineLatest([
      activeTutorial$,
      growthBookInitialized$,
    ]).pipe(
      map(([activeTutorial]) => !!activeTutorial),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // Feature flag: fab-move-to-avatar-menu - when true, hide FABs immediately (relocate to menu)
    // Wait for GrowthBook to initialize before evaluating the feature flag
    const fabMoveToAvatarMenu$ = combineLatest([
      this.growthbookService.getFeature<boolean>('fab-move-to-avatar-menu', false),
      growthBookInitialized$,
    ]).pipe(
      map(([fabMoveToAvatarMenu]) => fabMoveToAvatarMenu === true), // Explicit boolean check
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // Relocation rule: hide FABs when feature flag is enabled (relocate to menu)
    const hideStartTutorialFab$ = combineLatest([
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([fabMoveToAvatarMenu]) => {
        return fabMoveToAvatarMenu;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // "Book a Call" FAB: hide when feature flag is enabled (relocate to menu)
    const hideBookCallFab$ = combineLatest([
      fabMoveToAvatarMenu$,
    ]).pipe(
      map(([fabMoveToAvatarMenu]) => {
        return fabMoveToAvatarMenu;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    // Final visibility: combine eligibility with relocation rules
    this.canShowStartTutorialWidget$ = combineLatest([
      eligibleStartTutorial$,
      hideStartTutorialFab$,
    ]).pipe(
      map(([eligible, hide]) => eligible && !hide),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.canShowBookCallWidget$ = combineLatest([
      eligibleBookCall$,
      hideBookCallFab$,
    ]).pipe(
      map(([eligible, hide]) => eligible && !hide),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.shouldShowTrialWidget$ = combineLatest([
      this.canShowTrialWidget$,
      this.isPathExcluded$,
    ]).pipe(
      map(([canShowTrialWidget, isPathExcluded]) =>
        canShowTrialWidget && !isPathExcluded,
      ),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    this.shouldShowFloatingWidget$ = combineLatest([
      this.shouldShowTrialWidget$,
      this.canShowStartTutorialWidget$,
      this.canShowBookCallWidget$,
      growthBookInitialized$,
    ]).pipe(
      map(([showTrial, showStartTutorial, showBookCall]) => {
        const hasVisibleWidgets = showTrial || showStartTutorial || showBookCall;

        return hasVisibleWidgets;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );

    combineLatest([
      this.canShowStartTutorialWidget$,
      this.canShowBookCallWidget$,
      this.isDemoDialogOpen$,
    ]).pipe(
      untilDestroyed(this),
    ).subscribe(([showStartTutorial, showBookCall, isDemoDialogOpen]) => {
      this.controlHubSpotChatPosition(showStartTutorial, showBookCall, isDemoDialogOpen);
    });

    this.canShowStartTutorialWidget$.pipe(
      untilDestroyed(this),
    ).subscribe(value => {
      this.canShowStartTutorialWidget = value;
    });

    this.canShowBookCallWidget$.pipe(
      untilDestroyed(this),
    ).subscribe(value => {
      this.canShowBookCallWidget = value;
    });

    this.initializeTourBreakpoint();
  }

  private initializeTourBreakpoint(): void {
    // Check if screen is wide enough for tour (minimum 701px)
    this.isScreenWideEnoughForTour$ = this.breakpointObserver
      .observe(['(min-width: 701px)'])
      .pipe(
        map(result => result.matches),
        distinctUntilChanged(),
        untilDestroyed(this),
      );

    // Adjust widget positioning when screen size changes
    this.isScreenWideEnoughForTour$.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      this.adjustForMobile();
    });
  }

  private checkPathExclusion(): void {
    const excludedPath = ['new-billing'];

    this.isPathExcluded$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(0),
      map(() => {
        let child = this.router.routerState.snapshot.root;
        while (child.firstChild) {
          child = child.firstChild;
        }
        const paths = child.pathFromRoot.map(x => x?.routeConfig?.path || '').filter(x => x);
        const activeUrl = paths.join('/');
        const isExcluded = excludedPath.includes(activeUrl);

        this.isPathExcluded = isExcluded;

        return isExcluded;
      }),
      distinctUntilChanged(),
      untilDestroyed(this),
    );
  }

  private controlHubSpotChatPosition(showStartTutorial: boolean, showBookCall: boolean, isDemoDialogOpen: boolean): void {
    const widgetsToShow = [showStartTutorial, showBookCall].filter(Boolean);
    const widgetCount = widgetsToShow.length;

    if (isDemoDialogOpen) {
      document.body.classList.add('hide-hubspot-chat');
    } else {
      document.body.classList.remove('hide-hubspot-chat');
    }

    if (widgetCount > 1) {
      document.body.classList.add('has-several-floating-widget');
    } else {
      document.body.classList.remove('has-several-floating-widget');
    }

    if (widgetCount === 1) {
      document.body.classList.add('has-only-one-floating-widget');
    } else {
      document.body.classList.remove('has-only-one-floating-widget');
    }
  }


  onDemoDialogOpened(): void {
    // Don't set isDemoDialogOpen here - it's an @Input() that comes from app.component
    // The app.component will set it only if the tutorial actually opens
    // This prevents the FAB from being hidden when tutorial can't open (e.g., already completed)
    this.navatticDialogOpened.emit();
    this.startTutorialRequested.emit();
  }

  onDemoDialogClosed(): void {
    this.isDemoDialogOpen = false;
    this.isDemoDialogOpen$.next(false);
    this.navatticDialogClosed.emit();
  }

  getPosition() {
    return {
      top: Math.max(0, window.innerHeight - this.widgetHeight - 20),
      right: 20,
    };
  }

  ngAfterViewInit(): void {
    this.ensureInBounds();
    document.querySelector('#floating-widget-spacer')?.classList.add('enabled');
  }

  // Adjust the widget for mobile view
  public adjustForMobile(): void {
    // Use the same 701px breakpoint as our tour widget
    const isNarrowScreen = window.innerWidth < 701;

    if (isNarrowScreen) {
      // On narrow screens, make the widget full width and position it at the bottom
      this.widgetWidth = window.innerWidth;
      this.position.right = 0;
      this.position.top = window.innerHeight - this.widgetHeight - 20;
    } else {
      // On wide screens, revert to default widget width
      this.widgetWidth = 380;
      this.position = this.getPosition();
    }
    this.ensureInBounds();
  }

  public ensureInBounds(): void {
    const windowHeight = window.innerHeight;
    const isNarrowScreen = window.innerWidth < 701;

    if (!isNarrowScreen) {
      // Only enforce bounds for desktop - right positioning doesn't need left bounds
      this.position.top = Math.max(0, Math.min(this.position.top, windowHeight - this.widgetHeight));
    }
  }

  onMouseDown(event: MouseEvent): void {
    if (this.isMobile) return;
    this.isDragging = true;
    this.drag = false;

    // Calculate offset from the left edge of the widget
    const widgetLeft = window.innerWidth - this.position.right - this.widgetWidth;
    this.offsetX = event.clientX - widgetLeft;
    this.offsetY = event.clientY - this.position.top;
    event.preventDefault();
  }

  @HostListener('document:mouseup', ['$event'])
  onMouseUp(): void {
    this.isDragging = false;
    this.drag = false;
    this.isWidgetDragging.emit(false);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.isWidgetDragging.emit(true);
      this.drag = true;

      // Calculate new position based on mouse position and offset
      const newLeft = event.clientX - this.offsetX;
      const newTop = event.clientY - this.offsetY;

      // Convert left position to right position and apply bounds check
      this.position.right = Math.max(20, Math.min(
        window.innerWidth - newLeft - this.widgetWidth,
        window.innerWidth - this.widgetWidth - 20,
      ));
      this.position.top = Math.max(0, Math.min(newTop, window.innerHeight - this.widgetHeight));
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.position.right = 20;
    this.position.top = window.innerHeight - this.widgetHeight - 20;
    this.ensureInBounds();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widgetHeight'] || changes['widgetWidth']) {
      this.ensureInBounds();
    }

    if (changes['isDemoDialogOpen']) {
      this.isDemoDialogOpen$.next(this.isDemoDialogOpen);
    }
  }

  ngOnDestroy(): void {
    this.controlHubSpotChatPosition(false, false, false);
  }

  // Handle touch start
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    // Don't allow touch dragging if any widgets are showing
    // Check stored values synchronously
    if (this.canShowStartTutorialWidget || this.canShowBookCallWidget) {
      return;
    }
    const touch = event.touches[0];
    this.isDragging = true;
    this.touchMoved = false; // Reset touchMoved state

    // Calculate offset from the left edge of the widget
    const widgetLeft = window.innerWidth - this.position.right - this.widgetWidth;
    this.offsetX = touch.clientX - widgetLeft;
    this.offsetY = touch.clientY - this.position.top;
  }

  // Handle touch move
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      const touch = event.touches[0];

      // Calculate new position based on touch position and offset
      const newLeft = touch.clientX - this.offsetX;
      const newTop = touch.clientY - this.offsetY;

      // Convert left position to right position and apply bounds check
      this.position.right = Math.max(20, Math.min(
        window.innerWidth - newLeft - this.widgetWidth,
        window.innerWidth - this.widgetWidth - 20,
      ));
      this.position.top = Math.max(0, Math.min(newTop, window.innerHeight - this.widgetHeight));
      this.touchMoved = true; // Mark that a move occurred
      this.isWidgetDragging.emit(true);
      event.preventDefault();
    }
  }

  // Handle touch end
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (this.isDragging && !this.touchMoved) {
      event.stopPropagation();
    }
    this.isDragging = false;
    this.isWidgetDragging.emit(false);
  }

  onTrialWidgetPanelOpened(widgetHeight: number) {
    this.floatingTrialWidgetHeight = widgetHeight;
    this.widgetHeightChanged.emit(widgetHeight);

    const availableSpaceBelow = window.innerHeight - this.position.top - this.widgetHeight;

    if (availableSpaceBelow < widgetHeight) {
      // Not enough space below, position the widget to open upwards
      this.position.top = Math.max(0, this.position.top - (widgetHeight - availableSpaceBelow));
    }
    document.querySelector('#hubspot-messages-iframe-container')?.classList.add('expanded');
  }

  onTrialWidgetPanelClosed(): void {
    this.position.top = Math.max(0, window.innerHeight - this.widgetHeight - 20);
    document.querySelector('#hubspot-messages-iframe-container')?.classList.remove('expanded');
  }
}
